/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    SaveLocation,
    Location,
    BusinessAreaType,
    AddressAttributes,
    LocationStaffMember,
    DuplicateLocationDetails,
    LocationCopyArea,
    LocationDuplication,
    LocationDuplicationReviewItem,
    LOCATION_COPY_AREAS
} from 'model/location';
import {
    SaveLocationDto,
    LocationDto,
    AddressAttributesDto,
    LocationStaffDto,
    DuplicateLocationRequestDto,
    LocationDuplicationDto
} from './LocationDtos';

/**
 * Convert AddressAttributesDto to AddressAttributes
 */
const convertToAddressAttributes = (dto: AddressAttributesDto): AddressAttributes => {
    return {
        id: dto.id,
        marketplaceId: dto.marketplace_id,
        addressLineOne: dto.address_line_one,
        addressLineTwo: dto.address_line_two,
        city: dto.city,
        state: dto.state,
        zipcode: dto.zipcode,
        country: dto.country
    };
};

/**
 * Convert AddressAttributes to AddressAttributesDto
 */
const convertToAddressAttributesDto = (address: AddressAttributes): AddressAttributesDto => {
    return {
        id: address.id,
        marketplace_id: address.marketplaceId,
        address_line_one: address.addressLineOne,
        address_line_two: address.addressLineTwo,
        city: address.city,
        state: address.state,
        zipcode: address.zipcode,
        country: address.country
    };
};

/**
 * Convert LocationDto to Location Model
 */
export const convertToLocation = (dto: LocationDto): Location => {
    // API returns 'addresses' in GET responses but we use 'addresses_attributes' in our model
    const addressesData = dto.addresses || dto.addresses_attributes;

    return {
        id: dto.id,
        organizationId: dto.organization_id,
        businessName: dto.business_name,
        email: dto.email,
        website: dto.website,
        phone: dto.phone,
        businessAreas: dto.business_area_list as Array<BusinessAreaType>,
        active: dto.active,
        createdAt: new Date(dto.created_at),
        updatedAt: dto.updated_at ? new Date(dto.updated_at) : undefined,
        addressesAttributes: addressesData?.map(convertToAddressAttributes),
        // Use undefined when staffs is not provided so reducer can preserve existing value
        staffCount: dto.staffs !== undefined ? dto.staffs.length : undefined
    };
};

/**
 * Convert SaveLocation Model to SaveLocationDto
 */
export const convertToSaveLocationDto = (location: SaveLocation): SaveLocationDto => {
    return {
        id: location.id,
        active: location.active,
        user_id: location.userId,
        organization_id: location.organizationId,
        email: location.email,
        website: location.website,
        business_name: location.businessName,
        business_area_list: location.businessAreas,
        phone: location.phone,
        terms_of_service: location.termsOfService,
        privacy_policy: location.privacyPolicy,
        addresses_attributes: location.addressesAttributes?.map(convertToAddressAttributesDto)
    };
};

/**
 * Convert LocationStaffDto to LocationStaffMember Model
 */
export const convertToLocationStaffMember = (dto: LocationStaffDto): LocationStaffMember => {
    const firstName = dto.first_name ?? '';
    const lastName = dto.last_name ?? '';

    return {
        id: dto.id,
        userId: dto.user_id,
        firstName,
        lastName,
        name: [firstName, lastName].filter(Boolean).join(' '),
        role: dto.role?.name ?? '',
        schedule: dto.schedule,
        servicesCount: dto.services_count,
        email: dto.email,
        phone: dto.phone ?? '',
        active: dto.active
    };
};

/**
 * Convert the duplicate form to DuplicateLocationRequestDto. url_slug is never sent, the backend generates it.
 */
export const convertToDuplicateLocationRequestDto = (
    details: DuplicateLocationDetails,
    areas: Array<LocationCopyArea>
): DuplicateLocationRequestDto => {
    return {
        marketplace: {
            business_name: details.businessName.trim(),
            email: details.email.trim(),
            phone: details.phone,
            address: {
                address_line_one: details.addressLineOne.trim(),
                address_line_two: details.addressLineTwo.trim(),
                city: details.city.trim(),
                state: details.state.trim(),
                zipcode: details.zipcode.trim(),
                country: details.country.trim()
            }
        },
        copy: areas
    };
};

const isLocationCopyArea = (area: string): area is LocationCopyArea =>
    LOCATION_COPY_AREAS.some(option => option.id === area);

/**
 * Convert LocationDuplicationDto to LocationDuplication Model. Unknown areas are dropped so only known labels are shown.
 */
export const convertToLocationDuplication = (dto: LocationDuplicationDto): LocationDuplication => {
    return {
        status: dto.status,
        marketplaceId: dto.marketplace_id ?? null,
        copied: (dto.copied ?? []).filter(isLocationCopyArea),
        review: (dto.review ?? []).filter((item): item is LocationDuplicationReviewItem =>
            isLocationCopyArea(item.area)
        ),
        error: dto.error ?? null
    };
};
